import {
  Component,
  Host,
  Prop,
  Element,
  Event,
  EventEmitter,
  h
} from "@stencil/core";
import { MercuryBundles } from "@genexus/mercury";
import type {
  BreadcrumbModel,
  BreadcrumbItemModel,
  SelectedLink,
  GetImagePathCallback
} from "./types";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/icon",
  "utils/link",
  "utils/typography",
  "utils/spacing"
];

const DEFAULT_SELECTED_LINK: SelectedLink = { link: { url: undefined } };

@Component({
  tag: "gx-ide-breadcrumb",
  styleUrl: "breadcrumb.scss",
  shadow: true
})
export class GxIdeBreadcrumb {
  @Element() el: HTMLGxIdeBreadcrumbElement;

  /**
   * List of breadcrumb items to display.
   */
  @Prop() readonly model?: BreadcrumbModel;

  /**
   * aria-label for the navigation container (accessible-name attribute).
   */
  @Prop({ attribute: "accessible-name" }) readonly accessibleName?: string;

  /**
   * When true, disables all interaction with the breadcrumb.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false;

  /**
   * Text shown between items. Examples: "›" (recommended), "/", ">", "·", "•".
   * Default "›" (single right-pointing angle quotation mark, U+203A).
   */
  @Prop() readonly separator: string = "›";

  /**
   * Currently selected link (for highlighting the current page).
   */
  @Prop() readonly selectedLink: SelectedLink = DEFAULT_SELECTED_LINK;

  /**
   * When true, show visual indicator on the item matching selectedLink (selected-link-indicator attribute).
   */
  @Prop({ attribute: "selected-link-indicator" })
  readonly selectedLinkIndicator: boolean = false;

  /**
   * Optional callback to resolve image path for each item.
   */
  @Prop() readonly getImagePathCallback?: GetImagePathCallback;

  /**
   * Fired when the user clicks an item that has a link.
   */
  @Event() readonly hyperlinkClick?: EventEmitter<BreadcrumbItemModel>;

  /**
   * Fired when the user clicks an item without a link (button).
   */
  @Event() readonly buttonClick?: EventEmitter<BreadcrumbItemModel>;

  #isItemSelected = (item: BreadcrumbItemModel): boolean => {
    if (!this.selectedLinkIndicator) return false;
    const sel = this.selectedLink ?? DEFAULT_SELECTED_LINK;
    const matchById = sel.id !== undefined && item.id === sel.id;
    const matchByUrl =
      sel.link?.url !== undefined &&
      sel.link?.url !== "" &&
      item.link?.url === sel.link?.url;
    return matchById || matchByUrl;
  };

  #handleListClick = (event: MouseEvent) => {
    if (this.disabled) return;
    const target = event.target as HTMLElement;
    const itemEl = target.closest?.("gx-ide-breadcrumb-item") as
      | HTMLElement
      | undefined;
    if (!itemEl) return;
    const itemId = itemEl.getAttribute?.("data-item-id");
    const hasLink = itemEl.getAttribute?.("data-has-link") === "true";
    if (!itemId || !this.model) return;
    const item = this.model.find(i => i.id === itemId);
    if (!item) return;

    if (hasLink) {
      event.preventDefault();
      this.hyperlinkClick?.emit(item);
      return;
    }

    this.buttonClick?.emit(item);
  };

  render() {
    const items = this.model ?? [];
    const separator = this.separator;
    const ariaLabel = this.accessibleName ?? undefined;

    return (
      <Host class="breadcrumb" role="navigation" aria-label={ariaLabel}>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ol class="list" onClick={this.#handleListClick}>
          {items.map((item, index) => (
            <li key={item.id} class="list-item" role="presentation">
              <gx-ide-breadcrumb-item
                itemId={item.id}
                model={item}
                caption={item.caption}
                accessibleName={item.accessibleName}
                disabled={this.disabled || item.disabled}
                link={item.link}
                selected={this.#isItemSelected(item)}
                selectedLinkIndicator={this.selectedLinkIndicator}
                startImgSrc={item.startImgSrc}
                startImgType={item.startImgType}
                getImagePathCallback={this.getImagePathCallback}
              />
              {index < items.length - 1 && (
                <span class="separator body-regular-s" aria-hidden="true">
                  {separator}
                </span>
              )}
            </li>
          ))}
        </ol>
      </Host>
    );
  }
}
